"use client";

import { LoaderCircle, WandSparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { toast as sonnerToast } from "sonner";
import { facelessScriptGenerationLimits, quoteRuleConstants } from "@reelmagic/domain";
import { Button } from "@/components/ui/button";
import { CreditMark } from "@/components/icons/CreditMark";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { AppModal } from "@/components/ui/AppModal";
import { formatDurationFromMs } from "../../lib/format-duration";
import { generateFacelessScript } from "../../lib/generation/faceless";
import { reportCaughtError } from "../../lib/sentry/report-caught-error";
import {
  createFieldLabelClassName,
  createFieldLabelTextClassName,
} from "./CreatePageShell";

const facelessScriptGenerationCreditLabel =
  quoteRuleConstants.FACELESS_SCRIPT_GENERATION_CREDITS === 1
    ? "1 credit"
    : `${quoteRuleConstants.FACELESS_SCRIPT_GENERATION_CREDITS} credits`;

type FacelessScriptGenerationModalProps = {
  defaultDescription?: string;
  defaultTargetDurationSeconds?: number;
  isOpen: boolean;
  onClose: () => void;
  onScriptGenerated: (result: {
    description: string;
    script: string;
    targetDurationSeconds: number;
  }) => void;
};

export function FacelessScriptGenerationModal({
  defaultDescription = "",
  defaultTargetDurationSeconds =
    facelessScriptGenerationLimits.defaultTargetDurationSeconds,
  isOpen,
  onClose,
  onScriptGenerated,
}: FacelessScriptGenerationModalProps) {
  const [description, setDescription] = useState(defaultDescription);
  const [targetDurationSeconds, setTargetDurationSeconds] = useState(
    defaultTargetDurationSeconds,
  );
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setDescription(defaultDescription);
    setTargetDurationSeconds(defaultTargetDurationSeconds);
  }, [defaultDescription, defaultTargetDurationSeconds, isOpen]);

  const targetDurationLabel = formatDurationFromMs(targetDurationSeconds * 1000);

  async function handleSubmit() {
    if (isGenerating) {
      return;
    }

    const trimmedDescription = description.trim();

    if (!trimmedDescription) {
      sonnerToast.error("Describe the reel you want to create.");
      return;
    }

    setIsGenerating(true);

    try {
      const result = await generateFacelessScript({
        description: trimmedDescription,
        targetDurationSeconds,
      });
      onScriptGenerated({
        description: trimmedDescription,
        script: result.script,
        targetDurationSeconds,
      });
      onClose();
      sonnerToast.success("Script generated.");
    } catch (error: unknown) {
      reportCaughtError(error, {
        expectedCodes: ["functions/invalid-argument", "functions/unauthenticated"],
        operation: "generation.generate_script",
      });
      sonnerToast.error(
        error instanceof Error
          ? error.message
          : "Unable to generate script. Please try again.",
      );
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <AppModal
      body={
        <>
          <Label className={createFieldLabelClassName}>
            <span className={createFieldLabelTextClassName}>Reel Idea</span>
            <Input
              disabled={isGenerating}
              maxLength={facelessScriptGenerationLimits.maxDescriptionLength}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="E.g. The five strangest facts about the Mariana Trench"
              type="text"
              value={description}
            />
          </Label>

          <Label className={createFieldLabelClassName}>
            <span className={createFieldLabelTextClassName}>
              Target Duration: {targetDurationLabel}
            </span>
            <Slider
              aria-label="Target duration"
              className="mt-2"
              disabled={isGenerating}
              max={facelessScriptGenerationLimits.maxTargetDurationSeconds}
              min={facelessScriptGenerationLimits.minTargetDurationSeconds}
              onValueChange={([value]) =>
                setTargetDurationSeconds(
                  Math.round(
                    value ??
                      facelessScriptGenerationLimits.defaultTargetDurationSeconds,
                  ),
                )
              }
              step={5}
              value={[targetDurationSeconds]}
            />
          </Label>
        </>
      }
      bodyClassName="gap-5"
      description={
        <>
          Describe your reel and we&apos;ll draft a single-speaker voiceover
          script you can edit before generating the video.
          <span className="mt-3 flex items-center gap-1.5 font-semibold text-foreground">
            <CreditMark className="size-[1em] shrink-0" />
            {facelessScriptGenerationCreditLabel} will be charged.
          </span>
        </>
      }
      isDismissDisabled={isGenerating}
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Script"
    >
      <Button disabled={isGenerating} onClick={onClose} type="button" variant="outline">
        Cancel
      </Button>
      <Button
        disabled={isGenerating || !description.trim()}
        onClick={() => {
          void handleSubmit();
        }}
        type="button"
        variant="aura"
      >
        {isGenerating ? (
          <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
        ) : (
          <WandSparkles aria-hidden="true" className="size-4" />
        )}
        {isGenerating ? "Generating..." : "Generate Script"}
      </Button>
    </AppModal>
  );
}
